import React, { useMemo, useState } from "react";
import { Card, PageHeader } from "@pericles/framework-frontend";

import { runPortfolioBatch } from "../api/portfolioRunsClient";
import type { PortfolioBatchResult, PortfolioBatchSpec } from "../types/portfolioRuns";

import { Button, PageShell, Textarea } from "./ui";
const defaultSpec: PortfolioBatchSpec = {
  environment: "dev",
  stopRule: "halt_on_failure",
  items: [
    {
      workItemId: "",
      order: 1,
      dependsOn: [],
      spec: {},
    },
  ],
};

function formatJson(value: unknown): string {
  return JSON.stringify(value, null, 2);
}

export function PortfolioRunsScreen(): JSX.Element {
  const [rawSpec, setRawSpec] = useState(() => formatJson(defaultSpec));
  const [result, setResult] = useState<PortfolioBatchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  const parsedSpec = useMemo(() => {
    try {
      return JSON.parse(rawSpec) as PortfolioBatchSpec;
    } catch {
      return null;
    }
  }, [rawSpec]);

  async function handleRun() {
    if (!parsedSpec) {
      setError("Spec JSON is invalid. Please fix the JSON before running.");
      return;
    }

    setError(null);
    setIsRunning(true);
    setResult(null);

    try {
      const response = await runPortfolioBatch(parsedSpec);
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to run portfolio batch.");
    } finally {
      setIsRunning(false);
    }
  }

  return (
    <PageShell className="p-4">
      <PageHeader
        title="Portfolio runs"
        subtitle="Launch a batch of WorkItems using a minimal batch spec (order, dependencies, stop rules)."
      />

      <Card>
        <div className="flex flex-col gap-4 p-4">
          <label className="text-sm font-semibold text-slate-800" htmlFor="portfolio-batch-spec">
            Batch spec (JSON)
          </label>
          <Textarea
            id="portfolio-batch-spec"
            className="h-64 font-mono text-xs text-slate-800"
            value={rawSpec}
            onChange={(event) => setRawSpec(event.target.value)}
          />
          <div className="flex items-center gap-3">
            <Button type="button" onClick={() => void handleRun()} disabled={isRunning}>
              {isRunning ? "Running batch…" : "Run portfolio batch"}
            </Button>
            {error ? <span className="text-sm text-red-600">{error}</span> : null}
          </div>
        </div>
      </Card>

      {result ? (
        <Card>
          <div className="flex flex-col gap-4 p-4">
            <div>
              <div className="text-lg font-semibold text-slate-900">Batch summary</div>
              <div className="mt-2 text-sm text-slate-700">
                <div>Batch ID: {result.batchId}</div>
                <div>Status: {result.status}</div>
                <div>Stop rule: {result.stopRule}</div>
              </div>
            </div>
            <div className="grid gap-2 text-sm text-slate-700 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded border border-slate-100 bg-slate-50 p-2">Total: {result.summary.total}</div>
              <div className="rounded border border-slate-100 bg-slate-50 p-2">Succeeded: {result.summary.succeeded}</div>
              <div className="rounded border border-slate-100 bg-slate-50 p-2">Failed: {result.summary.failed}</div>
              <div className="rounded border border-slate-100 bg-slate-50 p-2">Skipped: {result.summary.skipped}</div>
            </div>
            <div className="flex flex-col gap-3">
              {result.groups.map((group) => (
                <div key={group.order} className="rounded border border-slate-100 bg-slate-50 p-3">
                  <div className="text-sm font-semibold text-slate-800">Order group: {group.order}</div>
                  <div className="mt-2 space-y-2">
                    {group.items.map((item) => (
                      <div key={`${group.order}-${item.workItemId}`} className="text-xs text-slate-700">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-semibold">{item.workItemId}</span>
                          <span className="rounded bg-slate-200 px-2 py-0.5 text-[10px] uppercase text-slate-700">
                            {item.status}
                          </span>
                          {item.runId ? <span>Run: {item.runId}</span> : null}
                        </div>
                        {item.error ? <div className="text-red-600">Error: {item.error}</div> : null}
                        {item.stopReason ? <div>Stop reason: {item.stopReason}</div> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      ) : null}
    </PageShell>
  );
}
